<template>
    <q-dialog v-model="showDialog" @hide="closeDialog">
        <q-card class="q-ma-md" style="width: 400px">
            <q-bar class="bg-primary q-mb-lg">
                <div class="text-white">
                    {{ $t('MFA Backup Codes') }}
                </div>
                <q-space />
                <q-btn
                    @click="closeDialog"
                    class="text-white"
                    icon="close"
                    dense
                    flat
                >
                    <q-tooltip>{{ $t('Close') }}</q-tooltip>
                </q-btn>
            </q-bar>
            <q-card-section
                class="q-gutter-y-md text-center"
                v-if="backupCodes.length === 0 && !isLoading"
            >
                <div class="row justify-center text-h6 q-mt-none">
                    {{ $t('Regenerate backup codes') }}
                </div>
                <div class="row justify-center text-secondary q-px-lg">
                    {{
                        $t(
                            'Enter your one-time password for this verification method to generate backup codes.'
                        )
                    }}
                </div>
                <div class="row justify-center">
                    <one-time-password-input
                        :length="passwordLength"
                        @update:modelValue="otp => (oneTimePassword = otp)"
                        @passwordIsValid="val => handleGenerateBackupCodes(val)"
                    />
                </div>

                <div class="row justify-center">
                    <div class="text-secondary q-px-lg">
                        {{
                            $t(
                                'Previous codes will no longer be valid once regenerated. Each code can only be used once.'
                            )
                        }}
                    </div>
                </div>
                <div class="row justify-between q-gutter-sm q-mt-md">
                    <span class="text-secondary">
                        <!-- Clickable text -->
                        <span
                            class="text-primary cursor-pointer"
                            @click="$emit('resendCodes')"
                            :class="{
                                disabled: emailOTPCoolDownSeconds > 0
                            }"
                        >
                            {{ $t('Resend code') }}
                            <span v-if="emailOTPCoolDownSeconds > 0">
                                ({{ emailOTPCoolDownSeconds }})
                            </span>
                        </span>
                    </span>
                    <span>
                        <q-btn
                            @click="closeDialog"
                            :label="$t('Cancel')"
                            outline
                            color="primary"
                            class="q-mr-sm"
                        />
                        <q-btn
                            @click="generateBackupCodes"
                            :label="$t('Submit')"
                            color="primary"
                            :disable="!isOtpValid"
                        />
                    </span>
                </div>
            </q-card-section>
            <q-card-section
                v-if="backupCodes.length > 0 && !isLoading"
                class="q-gutter-y-md text-center"
            >
                <div
                    class="row justify-center q-px-lg text-weight-medium q-mt-none"
                >
                    <div v-if="activationBackupCodes.length > 0">
                        {{ $t('MFA method has been activated') }}
                    </div>
                    <div v-else>
                        {{ $t('New backup codes have been generated') }}
                    </div>
                </div>

                <div class="row justify-center text-secondary q-px-lg">
                    {{
                        $t(
                            'If you ever lose access to this verification method, you can still login with any of these codes. You can only view this codes once, so keep them somewhere safe.'
                        )
                    }}
                </div>
                <div class="row justify-center">
                    <div
                        class="full-width"
                        v-for="code in backupCodes"
                        :key="code"
                    >
                        {{ code }}
                    </div>
                </div>
                <div class="row justify-between q-gutter-sm q-mt-md">
                    <q-btn
                        :label="$t('Copy')"
                        @click="copyCodes"
                        color="secondary"
                        icon="fa-light fa-copy"
                        size="sm"
                        outline
                    >
                        <q-tooltip>
                            {{ $t('Copy codes to clipboard') }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        @click="closeDialog"
                        :label="$t('Close')"
                        outline
                        color="primary"
                        id="close-backup-codes-btn"
                    />
                </div>
            </q-card-section>
            <q-card-section v-if="isLoading" style="height: 350px">
                <div class="row full-height items-center justify-center">
                    <q-spinner
                        class="q-mr-md"
                        color="primary"
                        size="3em"
                        :thickness="10"
                    />
                    <br />
                    <p class="q-mt-md">
                        {{ $t('Generating backup codes...') }}
                    </p>
                </div>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>
<script>
import { copyToClipboard } from 'quasar';
import OneTimePasswordInput from '@components/OneTimePasswordInput.vue';
export default {
    name: 'BackupCodesDialog',
    components: {
        OneTimePasswordInput
    },
    emits: ['close', 'resendCodes'],
    props: {
        show: {
            type: Boolean,
            default: false
        },
        passwordLength: {
            type: Number,
            default: 6
        },
        activationBackupCodes: {
            type: Array,
            default: () => []
        },
        method: {
            type: String,
            default: ''
        },
        emailOTPCoolDownSeconds: {
            type: Number,
            default: 0
        }
    },
    watch: {
        show: {
            immediate: true,
            handler(val) {
                this.showDialog = val;
            }
        }
    },
    data() {
        return {
            oneTimePassword: null,
            codes: [],
            isOtpValid: false,
            isLoading: false,
            showDialog: false
        };
    },
    computed: {
        backupCodes() {
            return this.codes.length === 0
                ? this.activationBackupCodes
                : this.codes;
        }
    },
    methods: {
        /**
         * Generate backup codes.
         */
        async generateBackupCodes() {
            // begin loading
            this.isLoading = true;
            try {
                const payload = {
                    code: this.oneTimePassword
                };
                const response = await this.$axios.post(
                    `auth/${this.method}/codes/regenerate/`,
                    payload
                );
                this.codes = response.data['backup_codes'];
                this.oneTimePassword = null;
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                if (error.code[0] === 'Code invalid or expired.') {
                    this.$notifyError(
                        this.$t('One-time password or backup code invalid.')
                    );
                } else {
                    this.$notifyError(
                        this.$t('Failed to generate backup codes')
                    );
                    console.error(error);
                }
            }
            // stop loading
            this.isLoading = false;
        },
        /**
         * Emit close event and clear codes.
         */
        closeDialog() {
            this.codes = [];
            this.$emit('close');
        },
        /**
         * Copy backup codes to clipboard.
         */
        copyCodes() {
            copyToClipboard(this.backupCodes.join('\n'))
                .then(() => {
                    this.$notifySuccess(this.$t('Codes copied to clipboard'));
                })
                .catch(() => {
                    this.$notifyError(
                        this.$t('Failed to copy codes to clipboard')
                    );
                });
        },
        /**
         * Handle OTP input to generate backup codes.
         * @param {Boolean} isValid
         */
        handleGenerateBackupCodes(isValid) {
            this.isOtpValid = isValid;
            if (isValid) {
                this.generateBackupCodes();
            }
        }
    }
};
</script>
